/**
 * Notifications tab for UserSettingsModal — browser permission step,
 * master toggle, registered devices, per-page notification cards
 * (grouped by department), and PWA install prompts.
 */

import React from 'react';
import {
  NOTIFICATION_PAGES_GROUPED,
  NOTIFICATION_PAGE_GROUPS,
  type NotificationPageId,
} from '../../../config/notificationPageRegistry';
import type { Location } from '../../../config/firebase';
import type { FCMTokenEntry, PermissionStatus } from '../../../hooks/useFCMToken';
import type { PWADebugInfo } from '../../../hooks/usePWAInstall';
import type { BrowserInfo, BrowserInstructions } from '../utils/browserInfo';
import type { useNotificationSettings } from '../hooks/useNotificationSettings';
import { BrowserPermissionSection } from './BrowserPermissionSection';
import { DevicesSection } from './DevicesSection';
import { RemoveDeviceConfirmModal } from './RemoveDeviceConfirmModal';
import { InstallAppSection } from './InstallAppSection';
import { NotificationPageCard } from './NotificationPageCard';

interface NotificationsTabProps {
  /** Settings state + actions from useNotificationSettings */
  controller: ReturnType<typeof useNotificationSettings>;
  locations: Location[];
  browserInfo: BrowserInfo;
  browserInstructions: BrowserInstructions;
  permission: {
    status: PermissionStatus;
    hasBrowserPermission: boolean;
    isRequesting: boolean;
    onRequest: () => void;
  };
  devices: {
    fcmTokens: FCMTokenEntry[];
    currentDeviceId: string | null;
    expanded: boolean;
    onToggleExpanded: () => void;
    pendingToggleDeviceId: string | null;
    onToggle: (deviceId: string, enabled: boolean) => void;
    onRequestRemove: (deviceId: string) => void;
    toRemove: string | null;
    isRemoving: boolean;
    onCancelRemove: () => void;
    onConfirmRemove: () => void;
  };
  install: {
    isInstalled: boolean;
    isAndroid: boolean;
    canInstall: boolean;
    isPrompting: boolean;
    onInstall: () => void;
    debugInfo: PWADebugInfo;
  };
  expandedLocations: Set<NotificationPageId>;
  expandedAdvanced: Set<NotificationPageId>;
  onToggleLocationsExpanded: (pageId: NotificationPageId) => void;
  onToggleAdvancedExpanded: (pageId: NotificationPageId) => void;
}

export const NotificationsTab: React.FC<NotificationsTabProps> = ({
  controller,
  locations,
  browserInfo,
  browserInstructions,
  permission,
  devices,
  install,
  expandedLocations,
  expandedAdvanced,
  onToggleLocationsExpanded,
  onToggleAdvancedExpanded,
}) => {
  const {
    settings,
    accessibleLocationsByPage,
    accessiblePages,
    handleMasterToggle,
    togglePage,
    toggleLocationForPage,
    toggleAllLocationsForPage,
    setReminderMinutes,
    toggleReminderEnabled,
    toggleNewEventEnabled,
    toggleStatusChangeEnabled,
    toggleStatusTransition,
    isPageEnabled,
    getLocationSummary,
  } = controller;

  const isDisabled = !settings.enabled;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-bold text-ink mb-2">Notification Settings</h3>
        <p className="text-ink-muted text-sm">
          Control how and when you receive alerts for different features.
        </p>
      </div>

      {/* Browser Permission - only show if not granted */}
      {!permission.hasBrowserPermission && (
        <BrowserPermissionSection
          permissionStatus={permission.status}
          browserInfo={browserInfo}
          browserInstructions={browserInstructions}
          isRequestingPermission={permission.isRequesting}
          onRequestPermission={permission.onRequest}
        />
      )}

      {/* Master Toggle */}
      <div className="bg-surface-muted rounded-lg p-4 border border-line">
        <div className="flex items-center gap-3">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center ${
              settings.enabled ? 'bg-success-soft text-success' : 'bg-surface-strong text-ink-muted'
            }`}
          >
            <i className={`fa-solid ${settings.enabled ? 'fa-check' : 'fa-bell-slash'}`}></i>
          </div>
          <div className="flex-1">
            <span className="font-semibold text-ink">Notifications</span>
            <p className="text-xs text-ink-muted">
              {settings.enabled ? 'Receiving alerts for enabled pages' : 'All notifications are paused'}
            </p>
          </div>
          <button
            onClick={handleMasterToggle}
            className={`relative w-12 h-7 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-focus focus:ring-offset-2 ${
              settings.enabled ? 'bg-selected' : 'bg-surface-deep'
            }`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-6 h-6 bg-surface rounded-full shadow-md transition-transform ${
                settings.enabled ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Devices Section - Collapsible, only show if there are registered devices */}
      {devices.fcmTokens.length > 0 && (
        <DevicesSection
          fcmTokens={devices.fcmTokens}
          currentDeviceId={devices.currentDeviceId}
          expanded={devices.expanded}
          onToggleExpanded={devices.onToggleExpanded}
          pendingToggleDeviceId={devices.pendingToggleDeviceId}
          onDeviceToggle={devices.onToggle}
          onRequestRemove={devices.onRequestRemove}
        />
      )}

      {/* Page Cards - Grouped by Department */}
      <div
        className={`space-y-4 transition-opacity ${
          !settings.enabled ? 'opacity-50 pointer-events-none' : ''
        }`}
      >
        {NOTIFICATION_PAGES_GROUPED.map(({ group, pages }) => {
          // Only render groups that have accessible pages
          const visiblePages = pages.filter((p) => accessibleLocationsByPage[p.id].length > 0);
          if (visiblePages.length === 0) return null;
          const groupMeta = NOTIFICATION_PAGE_GROUPS[group];
          return (
            <div key={group}>
              <div className="flex items-center gap-2 mb-2 px-1">
                <i className={`fa-solid ${groupMeta.icon} text-xs text-ink-faint`}></i>
                <span className="text-xs font-semibold text-ink-faint uppercase tracking-wider">{groupMeta.label}</span>
              </div>
              <div className="space-y-2">
                {pages.map((pageConfig) => (
                  <NotificationPageCard
                    key={pageConfig.id}
                    pageConfig={pageConfig}
                    pageSettings={settings.pages[pageConfig.id]}
                    accessibleLocations={accessibleLocationsByPage[pageConfig.id]}
                    locations={locations}
                    isEnabled={isPageEnabled(pageConfig.id)}
                    isDisabled={isDisabled}
                    locationSummary={getLocationSummary(pageConfig.id)}
                    isLocationsExpanded={expandedLocations.has(pageConfig.id)}
                    isAdvancedExpanded={expandedAdvanced.has(pageConfig.id)}
                    onTogglePage={togglePage}
                    onToggleLocationsExpanded={onToggleLocationsExpanded}
                    onToggleAdvancedExpanded={onToggleAdvancedExpanded}
                    onToggleLocation={toggleLocationForPage}
                    onToggleAllLocations={toggleAllLocationsForPage}
                    onSetReminderMinutes={setReminderMinutes}
                    onToggleReminderEnabled={toggleReminderEnabled}
                    onToggleNewEventEnabled={toggleNewEventEnabled}
                    onToggleStatusChangeEnabled={toggleStatusChangeEnabled}
                    onToggleStatusTransition={toggleStatusTransition}
                  />
                ))}
              </div>
            </div>
          );
        })}

        {accessiblePages.length === 0 && (
          <div className="text-center py-8 text-ink-faint">
            <i className="fa-solid fa-ban text-2xl mb-2"></i>
            <p className="text-sm">No notification sources available.</p>
            <p className="text-xs mt-1">You need page access to enable notifications.</p>
          </div>
        )}
      </div>

      {/* Remove Device Confirmation Modal */}
      {devices.toRemove && (
        <RemoveDeviceConfirmModal
          isCurrentDevice={devices.toRemove === devices.currentDeviceId}
          isRemoving={devices.isRemoving}
          onCancel={devices.onCancelRemove}
          onConfirm={devices.onConfirmRemove}
        />
      )}

      {/* Install App Section - Show on Android (always) and iOS (when not installed) */}
      {!install.isInstalled && (browserInfo.isIOS || install.isAndroid) && (
        <InstallAppSection
          browserInfo={browserInfo}
          isAndroid={install.isAndroid}
          canInstall={install.canInstall}
          isPrompting={install.isPrompting}
          onInstall={install.onInstall}
          debugInfo={install.debugInfo}
        />
      )}

      {/* Already Installed Message */}
      {install.isInstalled && (
        <div className="bg-success-subtle rounded-lg p-4 border border-success-border">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold bg-success-soft text-success">
              <i className="fa-solid fa-check"></i>
            </div>
            <div>
              <span className="font-semibold text-success-strong">App Installed</span>
              <p className="text-xs text-success">
                You're using BOTR Tools as an installed app
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
